"use client";

import Link from 'next/link';
import {
  FiArrowRight,
  FiCheckCircle,
  FiClock,
  FiDollarSign,
  FiLink,
  FiMessageCircle,
  FiTrendingUp,
  FiUsers,
} from 'react-icons/fi';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PROMOTEKIT_URL = 'https://instanttranscriber.promotekit.com';

const programHighlights = [
  {
    icon: FiDollarSign,
    title: '50% commission',
    body: 'Earn half of referred subscription revenue for eligible customers.',
  },
  {
    icon: FiClock,
    title: 'Up to 12 months',
    body: 'Get paid for up to a full year after a referred customer subscribes.',
  },
  {
    icon: FiTrendingUp,
    title: 'High-growth product',
    body: 'InstantTranscriber is growing fast, so we are paying aggressively to reward early partners.',
  },
];

const audienceItems = [
  'AI tool directories and SaaS review sites',
  'Creators teaching podcast, video, or YouTube workflows',
  'Consultants and agencies helping teams repurpose content',
  'Newsletters, blogs, and communities around productivity or AI',
  'Researchers, journalists, and education-focused creators',
  'Affiliate marketers with audiences that buy practical software',
];

const steps = [
  {
    title: 'Join on PromoteKit',
    body: 'Create or use your PromoteKit account and join the InstantTranscriber program.',
  },
  {
    title: 'Get your tracking link',
    body: 'PromoteKit gives you a unique referral link and dashboard for tracking clicks, conversions, and payouts.',
  },
  {
    title: 'Promote InstantTranscriber',
    body: 'Share reviews, tutorials, comparison pages, newsletter placements, YouTube descriptions, or resource lists.',
  },
  {
    title: 'Earn commission',
    body: 'When referred customers subscribe, your commission is tracked through PromoteKit.',
  },
];

const faqs = [
  {
    question: 'Where do I sign up?',
    answer: (
      <>
        Join through our PromoteKit affiliate page at{' '}
        <a href={PROMOTEKIT_URL}>instanttranscriber.promotekit.com</a>.
      </>
    ),
  },
  {
    question: 'Do affiliates need a PromoteKit account?',
    answer:
      'Yes. PromoteKit handles affiliate signup, tracking links, conversion tracking, and payout management for the program.',
  },
  {
    question: 'What should I promote?',
    answer:
      'InstantTranscriber is useful for audio and video transcription, meeting transcripts, podcast transcripts, captions, summaries, speaker labels, and export workflows.',
  },
  {
    question: 'Why is the commission so high?',
    answer:
      'We are in a growth phase and want strong partners to have a real upside for helping us reach more creators, teams, and professionals.',
  },
];

const affiliateStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': 'https://instanttranscriber.com/affiliate',
  name: 'InstantTranscriber Affiliate Program',
  description:
    'Earn 50% commission for up to 12 months by promoting InstantTranscriber.',
  url: 'https://instanttranscriber.com/affiliate',
  isPartOf: {
    '@id': 'https://instanttranscriber.com/#website',
  },
  about: {
    '@type': 'SoftwareApplication',
    name: 'InstantTranscriber',
    url: 'https://instanttranscriber.com',
    applicationCategory: 'BusinessApplication',
  },
};

export default function AffiliatePageClient() {
  return (
    <div>
      <Header />

      <main>
        <section className="affiliate-hero">
          <div className="container affiliate-hero-grid">
            <div className="affiliate-hero-copy">
              <p className="affiliate-eyebrow">InstantTranscriber Affiliate Program</p>
              <h1>Earn 50% commission for up to 12 months</h1>
              <p className="affiliate-subtitle">
                Promote a fast AI transcription product used for interviews, meetings, podcasts,
                research, captions, summaries, and content repurposing. We are paying a high
                commission because we want to grow with partners who can introduce InstantTranscriber
                to the right audience.
              </p>
              <div className="affiliate-actions">
                <a
                  href={PROMOTEKIT_URL}
                  className="btn hero-primary-btn"
                  data-analytics-event="affiliate_join_click"
                  data-analytics-cta-id="affiliate_join_primary"
                  data-analytics-cta-location="affiliate_hero"
                  rel="noopener noreferrer"
                >
                  Join on PromoteKit <FiArrowRight />
                </a>
                <Link href="/blog/instanttranscriber-affiliate-program" className="affiliate-secondary-link">
                  Read the launch post
                </Link>
              </div>
              <div className="affiliate-proof-row" aria-label="Affiliate program highlights">
                <span>50% commission</span>
                <span>Up to 12 months</span>
                <span>Powered by PromoteKit</span>
              </div>
            </div>

            <div className="affiliate-panel" aria-label="Affiliate program summary">
              <div className="affiliate-panel-top">
                <span>Commission</span>
                <strong>50%</strong>
              </div>
              <div className="affiliate-panel-row">
                <FiClock />
                <div>
                  <strong>Duration</strong>
                  <span>Up to 12 months per referred subscriber</span>
                </div>
              </div>
              <div className="affiliate-panel-row">
                <FiLink />
                <div>
                  <strong>Tracking</strong>
                  <span>Unique affiliate links through PromoteKit</span>
                </div>
              </div>
              <div className="affiliate-panel-row">
                <FiMessageCircle />
                <div>
                  <strong>Best angles</strong>
                  <span>AI transcription, podcast workflows, meetings, captions, research</span>
                </div>
              </div>
              <a href={PROMOTEKIT_URL} className="affiliate-panel-cta" rel="noopener noreferrer">
                Open PromoteKit signup
              </a>
            </div>
          </div>
        </section>

        <section className="affiliate-section">
          <div className="container">
            <div className="affiliate-section-header">
              <h2>Why promote InstantTranscriber?</h2>
              <p>
                Transcription has clear buying intent: people need to turn recordings into usable
                text, captions, notes, summaries, and documents. That makes the product easy to
                explain and useful across many audiences.
              </p>
            </div>
            <div className="affiliate-highlight-grid">
              {programHighlights.map((item) => {
                const Icon = item.icon;
                return (
                  <article className="affiliate-highlight-card" key={item.title}>
                    <Icon />
                    <h3>{item.title}</h3>
                    <p>{item.body}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="affiliate-section affiliate-section-alt">
          <div className="container affiliate-two-column">
            <div>
              <p className="affiliate-eyebrow">Audience fit</p>
              <h2>Who this affiliate program is for</h2>
              <p>
                The best affiliates can reach people who already work with audio, video, meetings,
                interviews, or content workflows. InstantTranscriber has a free plan, so your audience
                can try the product before upgrading.
              </p>
            </div>
            <ul className="affiliate-check-list">
              {audienceItems.map((item) => (
                <li key={item}>
                  <FiCheckCircle />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="affiliate-section">
          <div className="container">
            <div className="affiliate-section-header">
              <h2>How it works</h2>
              <p>Join once, get your link, and use PromoteKit to track the program.</p>
            </div>
            <div className="affiliate-steps">
              {steps.map((step, index) => (
                <article className="affiliate-step" key={step.title}>
                  <span>{index + 1}</span>
                  <h3>{step.title}</h3>
                  <p>{step.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="affiliate-section affiliate-section-alt">
          <div className="container affiliate-two-column">
            <div>
              <p className="affiliate-eyebrow">Promotional angles</p>
              <h2>Content that converts well</h2>
              <p>
                Affiliates usually do best when they show a real workflow: upload a meeting, generate
                a transcript, export captions, summarize an interview, or turn a podcast into written
                content.
              </p>
            </div>
            <div className="affiliate-angle-grid">
              <Link href="/transcribe" className="affiliate-angle-card">
                <FiUsers />
                <strong>Audio and video transcription</strong>
                <span>Show supported sources and daily workflows.</span>
              </Link>
              <Link href="/features/record-tab-audio" className="affiliate-angle-card">
                <FiMessageCircle />
                <strong>Meeting recording</strong>
                <span>Explain tab audio plus microphone capture.</span>
              </Link>
              <Link href="/export" className="affiliate-angle-card">
                <FiCheckCircle />
                <strong>Exports and captions</strong>
                <span>Cover TXT, DOCX, PDF, SRT, and VTT outputs.</span>
              </Link>
            </div>
          </div>
        </section>

        <section className="affiliate-section">
          <div className="container">
            <div className="affiliate-section-header">
              <h2>Affiliate FAQ</h2>
              <p>Quick answers before you join.</p>
            </div>
            <div className="affiliate-faq-grid">
              {faqs.map((item) => (
                <article className="affiliate-faq-item" key={item.question}>
                  <h3>{item.question}</h3>
                  <p>{item.answer}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="affiliate-final-cta">
          <div className="container">
            <h2>Ready to partner with InstantTranscriber?</h2>
            <p>
              Join on PromoteKit, get your referral link, and start promoting an AI transcription
              product with strong search intent and practical use cases.
            </p>
            <a
              href={PROMOTEKIT_URL}
              className="btn hero-primary-btn"
              data-analytics-event="affiliate_join_click"
              data-analytics-cta-id="affiliate_join_final"
              data-analytics-cta-location="affiliate_final"
              rel="noopener noreferrer"
            >
              Join on PromoteKit <FiArrowRight />
            </a>
          </div>
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(affiliateStructuredData),
        }}
      />

      <style jsx>{`
        .affiliate-hero {
          background:
            radial-gradient(circle at 20% 10%, rgba(16, 185, 129, 0.18), transparent 32%),
            linear-gradient(135deg, #f8fbff 0%, #eef2ff 100%);
          padding: 7rem 0 5rem;
        }

        .affiliate-hero-grid,
        .affiliate-two-column {
          display: grid;
          grid-template-columns: minmax(0, 1.05fr) minmax(320px, 0.75fr);
          gap: 3rem;
          align-items: center;
        }

        .affiliate-eyebrow {
          color: var(--primary-color);
          font-weight: 800;
          letter-spacing: 0;
          margin-bottom: 0.75rem;
          text-transform: uppercase;
          font-size: 0.85rem;
        }

        .affiliate-hero h1 {
          font-size: clamp(2.5rem, 5vw, 4.75rem);
          line-height: 1.02;
          max-width: 820px;
          margin-bottom: 1.25rem;
        }

        .affiliate-subtitle,
        .affiliate-section-header p,
        .affiliate-two-column p,
        .affiliate-final-cta p {
          color: var(--text-secondary);
          font-size: 1.1rem;
          line-height: 1.75;
        }

        .affiliate-subtitle {
          max-width: 760px;
        }

        .affiliate-actions {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 1rem;
          margin: 2rem 0 1.5rem;
        }

        .affiliate-actions .hero-primary-btn,
        .affiliate-final-cta .hero-primary-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
        }

        .affiliate-secondary-link {
          color: var(--primary-dark);
          font-weight: 800;
        }

        .affiliate-proof-row {
          display: flex;
          flex-wrap: wrap;
          gap: 0.7rem;
        }

        .affiliate-proof-row span {
          background: #ffffff;
          border: 1px solid var(--border-color);
          border-radius: 999px;
          color: var(--text-secondary);
          font-weight: 700;
          padding: 0.45rem 0.8rem;
        }

        .affiliate-panel {
          background: #ffffff;
          border: 1px solid rgba(79, 70, 229, 0.12);
          border-radius: var(--radius-lg);
          box-shadow: var(--shadow-lg);
          padding: 2rem;
        }

        .affiliate-panel-top {
          border-bottom: 1px solid var(--border-color);
          margin-bottom: 1.25rem;
          padding-bottom: 1.25rem;
        }

        .affiliate-panel-top span {
          color: var(--text-tertiary);
          display: block;
          font-weight: 700;
          margin-bottom: 0.35rem;
        }

        .affiliate-panel-top strong {
          color: var(--primary-color);
          display: block;
          font-size: 4rem;
          line-height: 1;
        }

        .affiliate-panel-row {
          display: flex;
          gap: 1rem;
          padding: 1rem 0;
        }

        .affiliate-panel-row svg {
          color: var(--accent-color);
          flex: 0 0 auto;
          font-size: 1.35rem;
          margin-top: 0.2rem;
        }

        .affiliate-panel-row strong,
        .affiliate-panel-row span {
          display: block;
        }

        .affiliate-panel-row strong {
          margin-bottom: 0.25rem;
        }

        .affiliate-panel-row span {
          color: var(--text-secondary);
        }

        .affiliate-panel-cta {
          background: var(--text-primary);
          border-radius: var(--radius-md);
          color: #ffffff;
          display: block;
          font-weight: 800;
          margin-top: 1.25rem;
          padding: 0.9rem 1rem;
          text-align: center;
        }

        .affiliate-section {
          padding: 5rem 0;
          background: var(--background-light);
        }

        .affiliate-section-alt {
          background: var(--background-alt);
        }

        .affiliate-section-header {
          margin: 0 auto 2.5rem;
          max-width: 760px;
          text-align: center;
        }

        .affiliate-section h2,
        .affiliate-final-cta h2 {
          font-size: clamp(2rem, 4vw, 3rem);
          line-height: 1.1;
          margin-bottom: 1rem;
        }

        .affiliate-highlight-grid,
        .affiliate-steps,
        .affiliate-faq-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 1.5rem;
        }

        .affiliate-highlight-card,
        .affiliate-step,
        .affiliate-faq-item,
        .affiliate-angle-card {
          background: #ffffff;
          border: 1px solid var(--border-color);
          border-radius: var(--radius-lg);
          box-shadow: var(--shadow-sm);
          padding: 1.5rem;
        }

        .affiliate-highlight-card svg {
          color: var(--primary-color);
          font-size: 2rem;
          margin-bottom: 1rem;
        }

        .affiliate-highlight-card h3,
        .affiliate-step h3,
        .affiliate-faq-item h3 {
          font-size: 1.25rem;
          margin-bottom: 0.6rem;
        }

        .affiliate-highlight-card p,
        .affiliate-step p,
        .affiliate-faq-item p,
        .affiliate-angle-card span {
          color: var(--text-secondary);
        }

        .affiliate-check-list {
          display: grid;
          gap: 0.9rem;
        }

        .affiliate-check-list li {
          align-items: flex-start;
          background: #ffffff;
          border: 1px solid var(--border-color);
          border-radius: var(--radius-md);
          display: flex;
          gap: 0.75rem;
          padding: 1rem;
        }

        .affiliate-check-list svg {
          color: var(--accent-color);
          flex: 0 0 auto;
          margin-top: 0.2rem;
        }

        .affiliate-step span {
          align-items: center;
          background: var(--primary-color);
          border-radius: 50%;
          color: #ffffff;
          display: inline-flex;
          font-weight: 800;
          height: 2.25rem;
          justify-content: center;
          margin-bottom: 1rem;
          width: 2.25rem;
        }

        .affiliate-angle-grid {
          display: grid;
          gap: 1rem;
        }

        .affiliate-angle-card {
          display: grid;
          grid-template-columns: 1.75rem minmax(0, 1fr);
          column-gap: 0.9rem;
          row-gap: 0.25rem;
          align-items: start;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }

        .affiliate-angle-card:hover {
          box-shadow: var(--shadow-md);
          transform: translateY(-2px);
        }

        .affiliate-angle-card svg {
          color: var(--primary-color);
          font-size: 1.45rem;
          grid-row: 1 / span 2;
          margin-top: 0.15rem;
        }

        .affiliate-angle-card strong,
        .affiliate-angle-card span {
          display: block;
          grid-column: 2;
          min-width: 0;
        }

        .affiliate-angle-card strong {
          line-height: 1.25;
        }

        .affiliate-faq-grid {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }

        .affiliate-faq-item a {
          color: var(--primary-color);
          font-weight: 800;
        }

        .affiliate-final-cta {
          background: linear-gradient(135deg, #111827 0%, #312e81 100%);
          color: #ffffff;
          padding: 5rem 0;
          text-align: center;
        }

        .affiliate-final-cta p {
          color: rgba(255, 255, 255, 0.82);
          margin: 0 auto 2rem;
          max-width: 760px;
        }

        @media (max-width: 900px) {
          .affiliate-hero-grid,
          .affiliate-two-column,
          .affiliate-highlight-grid,
          .affiliate-steps,
          .affiliate-faq-grid {
            grid-template-columns: 1fr;
          }

          .affiliate-hero {
            padding-top: 6rem;
          }
        }
      `}</style>
    </div>
  );
}
